export const CORRUPTED_SPECIAL_ENEMY_ID = 'corrupted_cultist';


export const ENEMY_START_FLOOR = {
  cultist: 1,
  hexbinder: 2,
  chymist: 3,
  mercenary: 3,
  lamp_keeper: 8,
  custodian: 8,
  'chained-penitent': 1,
  dreadbrand: 5,
  'dark-apostle': 6,
  'threshold_verifier': 6,
  // Boss-only enemies such as Monsignor and nested_horror are spawned separately.
};

export const ENEMY_LAST_FLOOR = {
  cultist: 2,
  'chained-penitent': 2,
  hexbinder: 3,
  mercenary: 5,
  chymist: 5,
  dreadbrand: 6,
  'dark-apostle': 7,
  'threshold_verifier': 7,
  lamp_keeper: 8,
  custodian: 8,
};

export const ROAMING_ENEMIES = [
  'cultist',
  'chained-penitent',
  'hexbinder',
  'chymist',
  'dreadbrand',
  'dark-apostle',
  'threshold_verifier',
  'mercenary',
  'lamp_keeper',
  'custodian',
];

// Deterministic per-floor spawn table for roaming enemies.
// Weights are used to build a guaranteed floor-wide roster, not a fresh roll per tile.
export const FLOOR_ENEMY_SPAWN_WEIGHTS = Object.freeze({
  1: Object.freeze([
    { id: 'cultist', weight: 50 },
    { id: 'chained-penitent', weight: 50 },
  ]),
  2: Object.freeze([
    { id: 'cultist', weight: 25 },
    { id: 'chained-penitent', weight: 25 },
    { id: 'hexbinder', weight: 50 },
  ]),
  3: Object.freeze([
    { id: 'hexbinder', weight: 25 },
    { id: 'mercenary', weight: 50 },
    { id: 'chymist', weight: 25 },
  ]),
  5: Object.freeze([
    { id: 'mercenary', weight: 25 },
    { id: 'chymist', weight: 50 },
    { id: 'dreadbrand', weight: 25 },
  ]),
  6: Object.freeze([
    { id: 'dreadbrand', weight: 50 },
    { id: 'dark-apostle', weight: 25 },
    { id: 'threshold_verifier', weight: 25 },
  ]),
  7: Object.freeze([
    { id: 'dark-apostle', weight: 50 },
    { id: 'threshold_verifier', weight: 50 },
  ]),
  8: Object.freeze([
    { id: 'lamp_keeper', weight: 50 },
    { id: 'custodian', weight: 50 },
  ]),
});

const enemyAssignmentCache = new WeakMap();

function getEligibleFloorEntries(floor) {
  const normalizedFloor = Math.max(1, Math.floor(Number(floor) || 1));
  const directEntries = FLOOR_ENEMY_SPAWN_WEIGHTS[normalizedFloor];
  if (Array.isArray(directEntries) && directEntries.length) return directEntries;

  // Fallback to the latest earlier configured non-boss floor so the system stays
  // stable if more floors are added without immediately wiring a new table.
  const configuredFloors = Object.keys(FLOOR_ENEMY_SPAWN_WEIGHTS)
    .map((value) => Math.floor(Number(value) || 0))
    .filter((value) => value > 0)
    .sort((a, b) => a - b);

  for (let index = configuredFloors.length - 1; index >= 0; index -= 1) {
    const configuredFloor = configuredFloors[index];
    if (configuredFloor <= normalizedFloor) {
      const fallbackEntries = FLOOR_ENEMY_SPAWN_WEIGHTS[configuredFloor];
      if (Array.isArray(fallbackEntries) && fallbackEntries.length) return fallbackEntries;
    }
  }

  return [];
}

function buildSeed({ x, y, floor, runSeed }) {
  const safeFloor = Math.max(1, Math.floor(Number(floor) || 1));
  const safeX = Math.floor(Number(x) || 0);
  const safeY = Math.floor(Number(y) || 0);
  const safeRunSeed = (Number(runSeed) || 0) >>> 0;

  return (
    ((safeRunSeed ^ (safeFloor * 2654435761)) ^
      (safeX * 374761393) ^
      (safeY * 668265263)) >>> 0
  );
}

function createRng(seed) {
  let state = (Number(seed) || 0) >>> 0;
  return function next() {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace(array, seed) {
  const rng = createRng(seed);
  for (let index = array.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(rng() * (index + 1));
    [array[index], array[swapIndex]] = [array[swapIndex], array[index]];
  }
  return array;
}

function normalizeEntriesForFloor(floor) {
  const safeFloor = Math.max(1, Math.floor(Number(floor) || 1));
  return getEligibleFloorEntries(safeFloor).filter(({ id, weight }) => {
    if (!ROAMING_ENEMIES.includes(id)) return false;
    if (!(Math.max(0, Number(weight) || 0) > 0)) return false;

    const startFloor = ENEMY_START_FLOOR[id] ?? 1;
    const lastFloor = ENEMY_LAST_FLOOR[id] ?? Number.POSITIVE_INFINITY;
    return startFloor <= safeFloor && safeFloor <= lastFloor;
  });
}

function getFallbackEligibleEnemies(floor) {
  const safeFloor = Math.max(1, Math.floor(Number(floor) || 1));
  return ROAMING_ENEMIES.filter((id) => {
    const startFloor = ENEMY_START_FLOOR[id] ?? 1;
    const lastFloor = ENEMY_LAST_FLOOR[id] ?? Number.POSITIVE_INFINITY;
    return startFloor <= safeFloor && safeFloor <= lastFloor;
  });
}

function collectEnemyTiles(map) {
  if (!Array.isArray(map)) return [];

  const enemyTiles = [];
  for (let y = 0; y < map.length; y += 1) {
    const row = map[y];
    if (!Array.isArray(row)) continue;
    for (let x = 0; x < row.length; x += 1) {
      if (row[x] === 'enemy') enemyTiles.push({ x, y });
    }
  }

  return enemyTiles;
}

function hashString(value) {
  const textValue = String(value || '');
  let hash = 2166136261;

  for (let index = 0; index < textValue.length; index += 1) {
    hash ^= textValue.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function compareEntriesForRoster(a, b, tieSeed) {
  const weightDelta = (Number(b.weight) || 0) - (Number(a.weight) || 0);
  if (weightDelta !== 0) return weightDelta;

  const aTie = hashString(`${a.id}:${tieSeed}`);
  const bTie = hashString(`${b.id}:${tieSeed}`);
  if (aTie !== bTie) return aTie - bTie;

  return String(a.id).localeCompare(String(b.id));
}

function buildCountsFromWeights(entries, enemyCount, seed) {
  if (!Array.isArray(entries) || !entries.length || enemyCount <= 0) return [];

  const totalWeight = entries.reduce(
    (sum, { weight }) => sum + Math.max(0, Number(weight) || 0),
    0,
  );
  if (!(totalWeight > 0)) return [];

  const safeEnemyCount = Math.max(0, Math.floor(Number(enemyCount) || 0));
  const counts = new Array(entries.length).fill(0);

  if (safeEnemyCount >= entries.length) {
    for (let index = 0; index < entries.length; index += 1) counts[index] = 1;

    const remainingSlots = safeEnemyCount - entries.length;
    if (remainingSlots <= 0) return counts;

    const remainders = entries.map(({ weight }, index) => {
      const exactShare = (remainingSlots * Math.max(0, Number(weight) || 0)) / totalWeight;
      const baseCount = Math.floor(exactShare);
      counts[index] += baseCount;
      return {
        index,
        remainder: exactShare - baseCount,
        tieBreaker: hashString(`${entries[index]?.id}:${seed}:${remainingSlots}`),
      };
    });

    let extras = safeEnemyCount - counts.reduce((sum, count) => sum + count, 0);

    remainders.sort((a, b) => {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      if (a.tieBreaker !== b.tieBreaker) return a.tieBreaker - b.tieBreaker;
      return a.index - b.index;
    });

    for (let index = 0; index < extras; index += 1) {
      counts[remainders[index % remainders.length].index] += 1;
    }

    return counts;
  }

  const rankedEntries = entries
    .map((entry, index) => ({ ...entry, index }))
    .sort((a, b) => compareEntriesForRoster(a, b, seed));

  for (let index = 0; index < safeEnemyCount; index += 1) {
    counts[rankedEntries[index].index] += 1;
  }

  return counts;
}

function buildFallbackAssignments(enemyTiles, floor, runSeed) {
  const eligibleFallback = getFallbackEligibleEnemies(floor);
  const assignments = new Map();
  if (!enemyTiles.length) return assignments;
  if (!eligibleFallback.length) {
    enemyTiles.forEach(({ x, y }) => assignments.set(`${x},${y}`, 'cultist'));
    return assignments;
  }

  const shuffledTiles = enemyTiles.map((tile) => ({ ...tile }));
  shuffleInPlace(shuffledTiles, buildSeed({ x: 31, y: 17, floor, runSeed }));

  shuffledTiles.forEach(({ x, y }, index) => {
    assignments.set(`${x},${y}`, eligibleFallback[index % eligibleFallback.length]);
  });

  return assignments;
}

export function getEnemyAssignmentsForMap({ map, floor, runSeed }) {
  const enemyTiles = collectEnemyTiles(map);
  if (!enemyTiles.length) return new Map();

  const safeFloor = Math.max(1, Math.floor(Number(floor) || 1));
  const safeRunSeed = (Number(runSeed) || 0) >>> 0;
  const configuredEntries = normalizeEntriesForFloor(safeFloor);
  if (!configuredEntries.length) {
    return buildFallbackAssignments(enemyTiles, safeFloor, safeRunSeed);
  }

  const counts = buildCountsFromWeights(
    configuredEntries,
    enemyTiles.length,
    buildSeed({ x: enemyTiles.length, y: configuredEntries.length, floor: safeFloor, runSeed: safeRunSeed }),
  );

  const roster = [];
  counts.forEach((count, index) => {
    const enemyId = configuredEntries[index]?.id;
    for (let i = 0; i < count; i += 1) {
      roster.push(enemyId);
    }
  });

  if (!roster.length) return buildFallbackAssignments(enemyTiles, safeFloor, safeRunSeed);

  const shuffledRoster = roster.slice();
  shuffleInPlace(
    shuffledRoster,
    buildSeed({ x: roster.length, y: 53, floor: safeFloor, runSeed: safeRunSeed }),
  );

  const shuffledTiles = enemyTiles.map((tile) => ({ ...tile }));
  shuffleInPlace(
    shuffledTiles,
    buildSeed({ x: enemyTiles.length, y: 97, floor: safeFloor, runSeed: safeRunSeed }),
  );

  const assignments = new Map();
  shuffledTiles.forEach(({ x, y }, index) => {
    assignments.set(`${x},${y}`, shuffledRoster[index % shuffledRoster.length] || configuredEntries[0].id);
  });

  return assignments;
}

function getCachedEnemyAssignmentsForMap({ map, floor, runSeed }) {
  if (!Array.isArray(map)) return null;

  const safeFloor = Math.max(1, Math.floor(Number(floor) || 1));
  const safeRunSeed = (Number(runSeed) || 0) >>> 0;
  const cacheKey = `${safeFloor}:${safeRunSeed}`;

  let cachedByMap = enemyAssignmentCache.get(map);
  if (!cachedByMap) {
    cachedByMap = new Map();
    enemyAssignmentCache.set(map, cachedByMap);
  }

  if (!cachedByMap.has(cacheKey)) {
    cachedByMap.set(
      cacheKey,
      getEnemyAssignmentsForMap({ map, floor: safeFloor, runSeed: safeRunSeed }),
    );
  }

  return cachedByMap.get(cacheKey) || null;
}

export function getEnemyIdForTile({ x, y, floor, runSeed, map = null }) {
  const safeFloor = Math.max(1, Math.floor(Number(floor) || 1));
  const safeX = Math.floor(Number(x) || 0);
  const safeY = Math.floor(Number(y) || 0);

  const cachedAssignments = getCachedEnemyAssignmentsForMap({
    map,
    floor: safeFloor,
    runSeed,
  });

  const mapAssignment = cachedAssignments?.get?.(`${safeX},${safeY}`);
  if (typeof mapAssignment === 'string' && mapAssignment.trim()) return mapAssignment;

  const eligibleFallback = getFallbackEligibleEnemies(safeFloor);
  const seed = buildSeed({ x: safeX, y: safeY, floor: safeFloor, runSeed });
  if (!eligibleFallback.length) return 'cultist';
  return eligibleFallback[seed % eligibleFallback.length];
}
